import Dialog from "./Dialog";
import { discardChanges, useConfirm } from "./ConfirmProvider";
import useUnsavedChanges from "../hooks/useUnsavedChanges";
import { useEffect, useId, useRef, useState } from "react";
import { parametresAPI } from "../services/api";
import { fiscalPreview } from "../utils/billing";
import { useForm } from "react-hook-form";

const EMPTY_CLIENT = { nom: "", societe: "", adresse: "", ville: "", code_postal: "", email: "", telephone: "", pays: "France", devise: "EUR", type_client: "a_confirmer", penalites_france_b2b: false, regime_tva: "auto", taux_horaire: "", delai_paiement: "", relances_en_pause: false };

const ClientForm = ({ client, onSubmit, onCancel }) => {
  const [defaults, setDefaults] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const submitLock = useRef(false);
  const initialized = useRef(false);
  useEffect(() => {
    let active = true;
    parametresAPI.get().then(({ data }) => { if (active) setDefaults(data); }).catch(() => {});
    return () => { active = false; };
  }, []);
  const formId = useId();
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm({ defaultValues: EMPTY_CLIENT });
  const clientType = watch("type_client");
  const currency = watch("devise");
  const country = watch("pays");
  const fiscal = watch("regime_tva");
  const typeRegistration = register("type_client");

  useEffect(() => {
    if (client) {
      reset({ ...EMPTY_CLIENT, ...client, societe: client.societe || "", telephone: client.telephone || "", type_client: client.type_client || "a_confirmer",
        penalites_france_b2b: client.type_client === "professionnel" && !!client.penalites_france_b2b });
    }
  }, [client, reset]);

  useEffect(() => {
    if (!client && defaults && !initialized.current) {
      initialized.current = true;
      const current = getValues();
      // Les valeurs saisies pendant le chargement restent conservées.
      reset({ ...EMPTY_CLIENT, taux_horaire: current.devise === defaults.devise ? defaults.taux_horaire_defaut : "",
        delai_paiement: defaults.delai_paiement }, { keepDirtyValues: true });
    }
  }, [client, defaults, getValues, reset]);

  const confirm = useConfirm();
  useUnsavedChanges(isDirty);
  const close = async () => {
    if (isSubmitting) return;
    if (!isDirty || await confirm(discardChanges)) onCancel();
  };

  const handleFormSubmit = async (data) => {
    if (submitLock.current || (client && !isDirty)) return;
    submitLock.current = true;
    setSubmitError("");
    try {
      const result = await onSubmit({ ...data, taux_horaire: Number(data.taux_horaire), delai_paiement: Number(data.delai_paiement) });
      if (result !== false) reset(data);
    } catch (failure) {
      const detail = failure?.response?.data?.detail;
      setSubmitError(typeof detail === "string" ? detail : "Impossible d'enregistrer le client. Vos modifications sont conservées.");
    } finally { submitLock.current = false; }
  };

  return (
    <Dialog footer={<>
            <button type="button" onClick={close} disabled={isSubmitting} className="btn-secondary">
              Annuler
            </button>
            <button form={formId} type="submit" disabled={isSubmitting || (client && !isDirty) || (!client && !defaults)} className="btn-primary">
              {isSubmitting ? "Enregistrement..." : client ? "Mettre à jour" : "Créer le client"}
            </button>
           </>} title={client ? "Modifier le client" : "Nouveau client"} onClose={close} busy={isSubmitting} size="max-w-2xl">
        <form id={formId}
          onSubmit={handleSubmit(handleFormSubmit)}
          className="space-y-6"
        >
          <fieldset disabled={isSubmitting} className="space-y-6">
          {/* Informations personnelles */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Informations personnelles
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="client-nom" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet *
                </label>
                <input aria-invalid={!!errors.nom} aria-describedby={errors.nom ? "error-clientform-nom" : undefined} id="client-nom"
                  type="text"
                  className="input-field"
                  {...register("nom", { required: "Le nom est requis" })}
                />
                {errors.nom && (
                  <p id="error-clientform-nom" className="text-red-600 text-sm mt-1">
                    {errors.nom.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="client-societe" className="block text-sm font-medium text-gray-700 mb-1">
                  Société
                </label>
                <input id="client-societe"
                  type="text"
                  className="input-field"
                  {...register("societe")}
                />
              </div>
            </div>
          </div>

          {/* Adresse */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Adresse</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="client-adresse" className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse *
                </label>
                <input aria-invalid={!!errors.adresse} aria-describedby={errors.adresse ? "error-clientform-adresse" : undefined} id="client-adresse"
                  type="text"
                  className="input-field"
                  {...register("adresse", {
                    required: "L'adresse est requise",
                  })}
                />
                {errors.adresse && (
                  <p id="error-clientform-adresse" className="text-red-600 text-sm mt-1">
                    {errors.adresse.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="client-ville" className="block text-sm font-medium text-gray-700 mb-1">
                    Ville *
                  </label>
                <input aria-invalid={!!errors.ville} aria-describedby={errors.ville ? "error-clientform-ville" : undefined} id="client-ville"
                    type="text"
                    className="input-field"
                    {...register("ville", { required: "La ville est requise" })}
                  />
                  {errors.ville && (
                    <p id="error-clientform-ville" className="text-red-600 text-sm mt-1">
                      {errors.ville.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="client-code_postal" className="block text-sm font-medium text-gray-700 mb-1">
                    Code postal *
                  </label>
                <input aria-invalid={!!errors.code_postal} aria-describedby={errors.code_postal ? "error-clientform-code_postal" : undefined} id="client-code_postal"
                    type="text"
                    className="input-field"
                    {...register("code_postal", {
                      required: "Le code postal est requis",
                    })}
                  />
                  {errors.code_postal && (
                    <p id="error-clientform-code_postal" className="text-red-600 text-sm mt-1">
                      {errors.code_postal.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="client-pays" className="block text-sm font-medium text-gray-700 mb-1">
                    Pays *
                  </label>
                <select aria-invalid={!!errors.pays} aria-describedby={errors.pays ? "error-clientform-pays" : undefined} id="client-pays" aria-label="Pays du client" className="input-field" {...register("pays", { required: "Le pays est requis" })} onChange={(event) => {
                    setValue("pays", event.target.value, { shouldDirty: true });
                    if (!client) {
                      const devise = event.target.value === "Canada" ? "CAD" : "EUR";
                      setValue("devise", devise, { shouldDirty: true });
                      setValue("taux_horaire", devise === defaults?.devise ? defaults.taux_horaire_defaut : "", { shouldDirty: true });
                    }
                    setValue("regime_tva", "auto", { shouldDirty: true });
                  }}>
                    <option value="France">France</option><option value="Canada">Canada</option>
                  </select>
                  {errors.pays && (
                    <p id="error-clientform-pays" className="text-red-600 text-sm mt-1">
                      {errors.pays.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="client-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input aria-invalid={!!errors.email} aria-describedby={errors.email ? "error-clientform-email" : undefined} id="client-email"
                  type="email"
                  className="input-field"
                  {...register("email", {
                    required: "L'email est requis",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Email invalide",
                    },
                  })}
                />
                {errors.email && (
                  <p id="error-clientform-email" className="text-red-600 text-sm mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="client-telephone" className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <input id="client-telephone"
                  type="tel"
                  className="input-field"
                  {...register("telephone")}
                />
              </div>

              <div>
                <label htmlFor="client-devise" className="block text-sm font-medium text-gray-700 mb-1">
                  Devise *
                </label>
                <select aria-invalid={!!errors.devise} aria-describedby={errors.devise ? "error-clientform-devise" : undefined} id="client-devise"
                  aria-label="Devise du client"
                  className="input-field"
                  {...register("devise", { required: "La devise est requise" })}
                  onChange={(event) => {
                    setValue("devise", event.target.value, { shouldDirty: true });
                    setValue("taux_horaire", "", { shouldDirty: true });
                  }}
                >
                  <option value="EUR">EUR (Euro)</option>
                  <option value="CAD">CAD (Dollar canadien)</option>
                </select>
                {errors.devise && (
                  <p id="error-clientform-devise" className="text-red-600 text-sm mt-1">
                    {errors.devise.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4 space-y-3">
            <h3 className="font-medium text-gray-900">Conditions commerciales</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="text-sm text-gray-700">Tarif horaire ({currency} / heure) *
                <input aria-invalid={!!errors.taux_horaire} aria-describedby={errors.taux_horaire ? "error-clientform-taux_horaire" : undefined} type="number" step="0.01" min="0" className="input-field mt-1" {...register("taux_horaire", { valueAsNumber: true, required: "Renseignez le tarif convenu", min: 0 })} />
                {errors.taux_horaire && <span id="error-clientform-taux_horaire" className="text-red-600">Renseignez un tarif positif ou nul.</span>}
              </label>
              <label className="text-sm text-gray-700">Délai de paiement (jours) *
                <input aria-invalid={!!errors.delai_paiement} aria-describedby={errors.delai_paiement ? "error-clientform-delai_paiement" : undefined} type="number" min="1" max="365" step="1" className="input-field mt-1" {...register("delai_paiement", { valueAsNumber: true, required: true, min: 1, max: 365, validate: (v) => Number.isInteger(Number(v)) })} />
                {errors.delai_paiement && <span id="error-clientform-delai_paiement" className="text-red-600">Choisissez entre 1 et 365 jours entiers.</span>}
              </label>
            </div>
            <p className="text-xs text-gray-500">Tarif dans la devise du client, sans conversion automatique. Les paramètres globaux servent uniquement à préparer les nouveaux clients.</p>
          </div>

          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-medium text-gray-700">
              Nature du client
              <select className="input-field mt-1" {...typeRegistration} onChange={(event) => {
                typeRegistration.onChange(event);
                if (event.target.value !== "professionnel") {
                  setValue("penalites_france_b2b", false, { shouldDirty: true });
                  if (fiscal === "hors_champ_canada_b2b") setValue("regime_tva", "auto", { shouldDirty: true });
                }
              }}>
                <option value="a_confirmer">À qualifier</option>
                <option value="professionnel">Professionnel</option>
                <option value="particulier">Particulier</option>
                <option value="organisme_public">Organisme public</option>
              </select>
            </label>
            {clientType === "a_confirmer" && <p className="text-sm text-amber-800">Qualifiez la nature du client avant d'émettre un devis ou une facture.</p>}
            {clientType === "professionnel" && <label className="flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" className="mt-1" {...register("penalites_france_b2b")} />
              Appliquer le régime français entre professionnels aux nouvelles factures
            </label>}
            <p className="text-xs text-gray-500">
              Activez ce régime lorsque les conditions convenues prévoient les pénalités françaises
              et l'indemnité de 40 EUR. Le pays du client ne suffit pas à déterminer les règles.
              Les anciennes factures se confirment séparément dans le tableau de bord.
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 p-4 space-y-3">
            <label className="text-sm font-medium text-gray-700">Régime TVA des prestations
              <select className="input-field mt-1" {...register("regime_tva")}>
                <option value="auto">Automatique (prestations relevant de la règle générale)</option>
                <option value="franchise_base">Franchise en base française, régime validé</option>
                {country === "Canada" && clientType === "professionnel" && <option value="hors_champ_canada_b2b">Hors champ français, professionnel canadien</option>}
                <option value="a_valider">Cas particulier à valider avant émission</option>
              </select>
            </label>
            <p className="text-xs text-gray-500">Le choix doit correspondre aux prestations convenues. Les règles particulières et les taxes canadiennes ne sont pas calculées par l'application.</p>
            <div className="rounded bg-gray-50 p-3 text-sm" aria-live="polite"><strong>Mention prévue : </strong>{fiscalPreview({ pays: country, type_client: clientType, regime_tva: fiscal }, defaults)}</div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" {...register("relances_en_pause")} />
            Suspendre les relances automatiques pour ce client
          </label>

          {submitError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{submitError}</p>}
          <p role="status" className="text-xs text-gray-500">{isDirty ? "Modifications non enregistrées" : client ? "Aucune modification" : "Renseignez les informations du client"}</p>

          </fieldset>
        </form>
    </Dialog>
  );
};

export default ClientForm;
